import React, { useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import UAvatar from 'modules/user/UAvatar.component';
import { Row, View, SecondaryText, CommunityLink, InlineLink } from 'modules/styled';
import { SIDE_NAV_PADDING } from 'styles/layout';
import { goToTopic } from 'modules/navigation/navigation.actions';
import { getCommunityMembers } from 'modules/community/community.actions';
import { Markdown } from 'modules/comment';
import { HELVETICA_REGULAR } from 'styles/fonts';

CommunityActive.propTypes = {
  community: PropTypes.object,
  children: PropTypes.node,
  members: PropTypes.array,
  view: PropTypes.object,
  auth: PropTypes.object,
  screenSize: PropTypes.number,
  viewCommunityMembers: PropTypes.func,
  showSettings: PropTypes.func,
  userCommunities: PropTypes.array,
  userMemberships: PropTypes.array,
};

export default memo(CommunityActive);

function CommunityActive({
  community,
  children,
  members,
  view,
  screenSize,
  auth,
  viewCommunityMembers,
  userCommunities,
  showSettings,
  userMemberships,
}) {
  const dispatch = useDispatch();
  const discoverTopic = useSelector((state) => state?.navigation?.discover?.tag);

  useEffect(() => {
    if (!members.length) dispatch(getCommunityMembers({ slug: community.slug }));
  }, []); // eslint-disable-line

  const topics = community?.topics || [];
  const totalMembers = community.memberCount || 0;
  const userId = auth.user?._id;
  const allMembers = members.filter((member) => member.embeddedUser._id !== userId);

  let isMember;
  if (auth.user) {
    isMember = userCommunities.find((_id) => _id === community._id);
    if (isMember) {
      allMembers.unshift({ _id: userId, embeddedUser: auth.user });
    }
  }
  const memberShip = userMemberships.find((m) => m.communityId === community._id);
  const isSuperAdmin =
    (memberShip && memberShip.superAdmin) || auth?.user?.role === 'admin';

  const limitedMembers = allMembers.slice(0, screenSize ? 14 : 12);
  const sort = view?.sort || 'new';
  return (
    <View sx={{ bg: 'white' }}>
      <View sx={{ mt: [2, SIDE_NAV_PADDING] }} />
      {children}
      <View
        sx={{
          px: [2, SIDE_NAV_PADDING],
          pb: [2, 4],
          variant: 'borders.navBorder',
          borderBottomWidth: 1,
        }}
      >
        <View sx={{ mt: 4.001, ml: 5.5001 * 8 }}>
          {topics.map((topic) => {
            const active = topic === discoverTopic;
            return (
              <View key={topic} sx={{ mt: 1 }}>
                <InlineLink
                  c={active ? 'black' : 'grey'}
                  hc={'black'}
                  ac={'black'}
                  variant="text.communityLink"
                  nativeAction={() => dispatch(goToTopic(topic))}
                  to={`/${community.slug}/${sort}/${topic}`}
                >
                  #{topic}
                </InlineLink>
              </View>
            );
          })}
        </View>

        <View sx={{ mt: [2, SIDE_NAV_PADDING] }} />
        <Markdown
          style={{
            pMargin: 8,
            body: {
              fontSize: 14,
              lineHeight: 18,
              fontFamily: HELVETICA_REGULAR,
            },
          }}
          markdown={community.description}
        />

        <Row sx={{ mt: SIDE_NAV_PADDING, mb: 2, justifyContent: 'space-between' }}>
          <CommunityLink
            sx={{ color: 'black' }}
          >{`${totalMembers} Members`}</CommunityLink>
          <InlineLink inline={1} to="#" onClick={viewCommunityMembers}>
            <CommunityLink sx={{ color: 'blue' }}>See All</CommunityLink>
          </InlineLink>
        </Row>

        <Row sx={{ flexWrap: 'wrap' }}>
          {limitedMembers.map((member) => (
            <UAvatar key={member._id} user={member.embeddedUser} sx={{ mr: 1, mb: 1 }} />
          ))}
        </Row>

        {isSuperAdmin && (
          <SecondaryText
            sx={{
              mt: 2,
              color: 'blue',
              py: 0.5,
            }}
            onPress={showSettings}
            key={'settings_'}
          >
            Settings
          </SecondaryText>
        )}
      </View>
    </View>
  );
}
